import React, { SyntheticEvent } from 'react';
import './TrackDisplay.scss';
import SwipeToDelete from './SwipeToDelete';

const PLAY_BUTTON      = <img src={process.env.PUBLIC_URL + "img/play-circle-fill.svg"} alt="Play" title="Play" />
const PAUSE_BUTTON     = <img src={process.env.PUBLIC_URL + "img/pause-circle-fill.svg"} alt="Pause" title="Pause" />
const SPOTIFY_BUTTON   = <img src={process.env.PUBLIC_URL + "img/spotify-logo.svg"} alt="Open in Spotify" title="Open in Spotify" />

interface TrackDisplayProps {
  albumArt?: string;
  artist: string;
  track: string;
  playbackUrl?: string;
  spotifyUrl?: string;
  preload?: HTMLAudioElement["preload"];
  onDelete?: () => void;
  isPlaying?: boolean;
  onPlayPauseClick?: (isPlaying: boolean) => void;
  onAudioStateChange?: (audio: HTMLAudioElement) => void;
}

interface TrackDisplayState {
  loadedPercent: number | null;
}

export default class TrackDisplay extends React.Component<TrackDisplayProps, TrackDisplayState> {

  private _audioRef: React.RefObject<HTMLAudioElement>;

  private _ref: React.RefObject<HTMLDivElement>;

  private _onUnmount: Array<() => void>;

  constructor(props: TrackDisplayProps) {
    super(props);
    this.state = {
      loadedPercent: 1
    }

    this._onUnmount = [];

    this._ref = React.createRef();
    this._audioRef = React.createRef();
  }

  componentWillUnmount() {
    for(let finalise of this._onUnmount) {
      finalise();
    }
  }

  componentDidMount(): void {
    this.updatePlayingState(); 
  }

  componentDidUpdate(prevProps: TrackDisplayProps) {
    this.updatePlayingState();
  }

  private async updatePlayingState() {
    console.log(`track ${this.props.artist} - ${this.props.track} is ${this.props.isPlaying ? 'playing' : 'not playing'}`);

    const audio = this._audioRef.current;
    if (!audio) return;

    if (this.props.isPlaying) {
      try {
        await audio.play();
      } catch (err) {
        // Try calling load and then play
        audio.load();
        audio.addEventListener('canplay', () => audio.play(), {once: true})
      }
    } else {
      audio.pause();
    }
  }

  audioProgressHandler(evt: SyntheticEvent<HTMLAudioElement, Event>) {
    const audio = evt.currentTarget;
    // console.log(audioLoaded(audio));
    
    this.setState({
      loadedPercent: audioLoaded(audio)
    });
  }

  blur() {
    this._ref?.current?.blur();
  }

  render() {
    let audioButton: React.ReactNode = '';
    if (this.props.playbackUrl) {
      const buttonArt = this.props.isPlaying ? PAUSE_BUTTON : PLAY_BUTTON;
      const loadedPercent = this.state.loadedPercent ?? 1;
      let extraClass = '';
      if (this.props.isPlaying && this.state.loadedPercent === null)
        extraClass = 'pulsing';
      else if (loadedPercent < 0.999)
        extraClass = 'loading';
      audioButton = <div className={`TrackDisplay-external-player ${extraClass}`} onPointerUp={(evt) => this._playPauseClick(evt)} style={{'--completion': `${loadedPercent * 100}%`} as React.CSSProperties}>{buttonArt}</div>;
    }

    let spotifyButton: React.ReactNode = '';
    if (this.props.spotifyUrl) {
      spotifyButton = <a className="TrackDisplay-external-player" href={this.props.spotifyUrl} target="_blank" rel="noreferrer" onPointerUp={(evt) => evt.stopPropagation()}>
        {SPOTIFY_BUTTON}
      </a>;
    }

    let albumArt: React.ReactNode = '';
    if (this.props.albumArt) {
      albumArt = <img src={this.props.albumArt} alt="" />;
    }

    const container = <div className="TrackDisplay-container">
            <div className="TrackDisplay-picture">
              {albumArt}
            </div>
            <div className="TrackDisplay-details">
              <div className="TrackDisplay-artist">{this.props.artist}</div>
              <div className="TrackDisplay-track">{this.props.track}</div>
            </div>
            <div className="TrackDisplay-links" onPointerDown={(evt) => evt.stopPropagation()}>
              {audioButton}
              {spotifyButton}
            </div>
            <audio src={this.props.playbackUrl} preload={this.props.preload ?? 'none'}
            ref={this._audioRef}
            onEnded={evt => this.props.onAudioStateChange?.(evt.currentTarget)}
            onPlay={evt => this.props.onAudioStateChange?.(evt.currentTarget)}
            onPause={evt => this.props.onAudioStateChange?.(evt.currentTarget)}
            onProgress={evt => this.audioProgressHandler(evt)}
             />
          </div>

    if (this.props.onDelete) {
      let deleteButton = <div className="TrackDisplay-delete-button" aria-label="Delete" onPointerUp={(evt) => this._deleteClick(evt)}></div>
      return (
        <div className="TrackDisplay-wrapper" ref={this._ref}>
          <SwipeToDelete onDelete={this.props.onDelete} deleteText="Remove from shortlist">
            {container}
          </SwipeToDelete>
          {deleteButton}
        </div>
      );
    } else {
      return (<div className="TrackDisplay-wrapper" ref={this._ref}>
        {container}
      </div>);
    }
  }

  _playPauseClick(evt: React.SyntheticEvent<any, PointerEvent>) {
    evt.stopPropagation();
    this.props.onPlayPauseClick?.(!this.props.isPlaying);
  }

  _deleteClick(evt: React.SyntheticEvent<any, PointerEvent>) {
    evt.stopPropagation();
    this.props.onDelete?.();
  }
}


function audioLoaded(audio: HTMLAudioElement): number | null {
  if (isNaN(audio.duration)) {
    return null;
  }
  const buffered = audio.buffered;
  let total = 0;
  for(let i = 0; i < buffered.length; i++) {
    total += buffered.end(i) - buffered.start(i);
  }
  
  return total / audio.duration;
}